'use client';

import { forwardRef, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

export const Fieldset = forwardRef<HTMLFieldSetElement, React.ComponentPropsWithoutRef<'fieldset'>>(
  ({ className, ...props }, ref) => {
    return <fieldset ref={ref} className={clsx(className, 'space-y-4')} {...props} />;
  }
);
Fieldset.displayName = 'Fieldset';

export const Field = forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={clsx(className, 'flex flex-col gap-1')} {...props} />;
  }
);
Field.displayName = 'Field';

export const Label = forwardRef<HTMLLabelElement, React.ComponentPropsWithoutRef<'label'>>(
  ({ className, ...props }, ref) => {
    return <label ref={ref} className={clsx(className, 'text-sm font-medium text-gray-700')} {...props} />;
  }
);
Label.displayName = 'Label';

const inputClasses = 'block w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm';

export const Input = forwardRef<HTMLInputElement, React.ComponentPropsWithoutRef<'input'>>(
  ({ className, ...props }, ref) => {
    return <input ref={ref} className={clsx(className, inputClasses)} {...props} />;
  }
);
Input.displayName = 'Input';

export const Select = forwardRef<HTMLSelectElement, React.ComponentPropsWithoutRef<'select'>>(
  ({ className, ...props }, ref) => {
    return <select ref={ref} className={clsx(className, inputClasses)} {...props} />;
  }
);
Select.displayName = 'Select';

export const Button = forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<'button'> & { plain?: boolean; color?: 'blue' | 'red' | 'green' | 'gray' } >(
  ({ className, plain, color = 'blue', ...props }, ref) => {
    const colorClasses = {
      blue: 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:outline-blue-600',
      red: 'bg-red-600 text-white hover:bg-red-700 focus-visible:outline-red-600',
      gray: 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus-visible:outline-gray-400',
    };
    const plainClasses = 'border-none bg-transparent text-gray-600 hover:bg-gray-100';
    const plainColorClasses = {
      red: 'text-red-600 hover:bg-red-50',
      blue: 'text-blue-600 hover:bg-blue-50',
      green: 'text-green-600 hover:bg-green-50',
      gray: 'text-gray-600 hover:bg-gray-100',
    };

    return (
      <button
        ref={ref}
        className={clsx(
          className,
          'inline-flex justify-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          plain ? `${plainClasses} ${plainColorClasses[color]}` : colorClasses[color]
        )}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function Dropdown({ options, value, onChange, disabled = false, placeholder = "Select an option", className }: DropdownProps) {
  const selectedOption = options.find(option => option.value === value);

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div className={clsx("relative", className)}>
        <Listbox.Button className={clsx(
          inputClasses,
          "relative cursor-default text-left",
          disabled && "cursor-not-allowed opacity-50"
        )}>
          <span className="block truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {options.map((option) => (
              <Listbox.Option
                key={option.value}
                className={({ active }) =>
                  clsx(
                    "relative cursor-default select-none py-2 pl-10 pr-4",
                    active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                  )
                }
                value={option.value}
              >
                {({ selected }) => (
                  <>
                    <span className={clsx("block truncate", selected ? "font-medium" : "font-normal")}>
                      {option.label}
                    </span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}
